import React, { lazy, Suspense } from "react";

import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import ProcurementStatusComponent from "@/main/components/shared/status/procurementStatusComponent";
import { SpinnerLoading } from "@/main/components/shared/styles";
import { getTranslateByKey } from "@/main/services/utils";
import { getProcurementStatusName } from "@/main/services/utilsProcurementStatus";

import CreateOrderBodyshopStepper from "./components/stepper/stepper";
import CreateOrderBodyshopStepsProvider, { useCreateOrderBodyshopSteps } from "./context";
import { TypeCreateOrderBodyshopStep } from "./types";
import { STEPS_CREATE_ORDER_BODYSHOP } from "./utilsSteps";
import * as S from "./styles";

const VehicleData = lazy(() => import("./steps/vehicleData/vehicleData"));
const DamageArea = lazy(() => import("./steps/damageArea/damageArea"));
const DamageParts = lazy(() => import("./steps/damageParts/damageParts"));
const Summary = lazy(() => import("./steps/summary/summary"));

interface IMapSteps {
	component: React.LazyExoticComponent<React.FC>;
	translateTitleStep: string;
	translateSubtitleStep: string;
}

const MAP_STEPS_NAME: Record<TypeCreateOrderBodyshopStep, IMapSteps> = {
	VehicleData: {
		component: VehicleData,
		translateTitleStep: "information.vehicleData",
		translateSubtitleStep: "",
	},
	DamageArea: {
		component: DamageArea,
		translateTitleStep: "information.damageArea",
		translateSubtitleStep: "",
	},
	DamageParts: {
		component: DamageParts,
		translateTitleStep: "information.damageParts",
		translateSubtitleStep: "",
	},
	Summary: {
		component: Summary,
		translateTitleStep: "information.summary",
		translateSubtitleStep: "",
	},
};

function buildStepEyebrow(currentPosition: number, total: number) {
	return getTranslateByKey("information.stepXofN")
		.replace("{0}", String(currentPosition))
		.replace("{1}", String(total));
}

function CreateOrderBodyshopSteps() {
	const {
		currentStep,
		completedSteps,
		isLoadingQuote,
		hasErrorQuote,
		isQuoteReadOnly,
		isStepNavigationLocked,
		quoteBlockedReasonKey,
		stepHead,
		canGoBack,
		retryLoadQuote,
		navigateToStep,
		goBack,
	} = useCreateOrderBodyshopSteps();

	if (isLoadingQuote) {
		return (
			<S.Container>
				<S.LoadingArea>
					<SpinnerLoading size="large" />
				</S.LoadingArea>
			</S.Container>
		);
	}

	if (hasErrorQuote) {
		return (
			<S.Container>
				<S.LoadingArea>
					<S.ErrorArea>
						<span>{getTranslateByKey("information.somethingWrong")}</span>
						<SecondaryButton id="id-createOrderBodyshop-btn-retryLoadQuote" type="button" onClick={retryLoadQuote}>
							{getTranslateByKey("information.tryAgain")}
						</SecondaryButton>
					</S.ErrorArea>
				</S.LoadingArea>
			</S.Container>
		);
	}

	const StepComponent = MAP_STEPS_NAME[currentStep].component;
	const translatesSteps: Record<TypeCreateOrderBodyshopStep, string> = Object.entries(MAP_STEPS_NAME).reduce(
		(acc, [key, value]) => {
			acc[key as TypeCreateOrderBodyshopStep] =
				value.translateTitleStep !== "" ? getTranslateByKey(value.translateTitleStep) : "";
			return acc;
		},
		{} as Record<TypeCreateOrderBodyshopStep, string>,
	);
	const stepEyebrow = buildStepEyebrow(
		STEPS_CREATE_ORDER_BODYSHOP.indexOf(currentStep) + 1,
		STEPS_CREATE_ORDER_BODYSHOP.length,
	);
	const subtitleKey = MAP_STEPS_NAME[currentStep].translateSubtitleStep;
	const stepSubtitle = subtitleKey ? getTranslateByKey(subtitleKey) : "";
	const headTitle = stepHead.title ?? translatesSteps[currentStep];
	const headSubtitle = stepHead.subtitle ?? stepSubtitle;
	const headStatusName = getProcurementStatusName(stepHead.idProcurementStatus ?? null);

	return (
		<S.Container>
			<S.SegProgArea>
				<S.ButtonBack
					type="button"
					id="id-createOrderBodyshop-btn-stepBack"
					aria-label={getTranslateByKey("information.back")}
					$isHidden={!canGoBack}
					onClick={goBack}
				>
					<S.IconBack />
				</S.ButtonBack>
				<CreateOrderBodyshopStepper
					currentStep={currentStep}
					completedSteps={completedSteps}
					isDisabled={isStepNavigationLocked}
					steps={STEPS_CREATE_ORDER_BODYSHOP}
					titleSteps={translatesSteps}
					onClickStep={(step) => navigateToStep(step as TypeCreateOrderBodyshopStep)}
				/>
			</S.SegProgArea>
			<S.Content>
				{!stepHead.isHidden && (
					<S.Head>
						<S.Eyebrow>{stepEyebrow}</S.Eyebrow>
						<S.TitleArea>
							<S.Title>{headTitle}</S.Title>
							{headStatusName && (
								<ProcurementStatusComponent
									status={{
										id: Number(stepHead.idProcurementStatus),
										name: headStatusName,
									}}
								/>
							)}
						</S.TitleArea>
						{headSubtitle && <S.Subtitle>{headSubtitle}</S.Subtitle>}
					</S.Head>
				)}
				{isQuoteReadOnly && <S.BlockedBanner>{getTranslateByKey(quoteBlockedReasonKey)}</S.BlockedBanner>}
				<Suspense fallback={<SpinnerLoading />}>
					<StepComponent />
				</Suspense>
			</S.Content>
			<S.Footer />
		</S.Container>
	);
}

function CreateOrderBodyshopScreen() {
	return (
		<CreateOrderBodyshopStepsProvider>
			<CreateOrderBodyshopSteps />
		</CreateOrderBodyshopStepsProvider>
	);
}

export default CreateOrderBodyshopScreen;
